<template>
  <b-dropdown
    v-if="hasMultipleAccounts"
    variant="link"
    no-caret
    right
    class="account-switcher"
  >
    <template v-slot:button-content>
      <div class="account-trigger">
        <i class="fas fa-user-circle"></i>
        <span>{{ customerData.userNumber }}</span>
        <i class="fas fa-chevron-down account-trigger__chevron"></i>
      </div>
    </template>

    <div class="dropdown-accounts">
      <div class="dropdown-header">
        <span class="dropdown-header__label">{{ $t('navigation.customer.account_switcher.accounts') }}</span>
      </div>

      <div
        @click="switchAccount(mainAccount.userNumber)"
        :class="['account-card', { 'account-card--active': isMainAccountActive, 'account-card--disabled': isMainAccountActive }]"
      >
        <div class="account-card__content">
          <span class="account-card__number">{{ mainAccount.userNumber }}</span>
          <span class="account-card__badge">{{ $t('navigation.customer.account_switcher.main') }}</span>
        </div>
        <i v-if="isMainAccountActive" class="fas fa-check-circle account-card__check"></i>
      </div>

      <div v-if="subaccounts.length" class="dropdown-divider"></div>

      <div
        v-for="sub in subaccounts"
        :key="sub.userNumber"
        @click="switchAccount(sub.userNumber)"
        :class="['account-card', { 'account-card--active': isSubaccountActive(sub.userNumber), 'account-card--disabled': isSubaccountActive(sub.userNumber) }]"
      >
        <div class="account-card__content">
          <span class="account-card__number">{{ sub.userNumber }}</span>
          <span class="account-card__badge">{{ $t('navigation.customer.account_switcher.subaccount') }}</span>
        </div>
        <i v-if="isSubaccountActive(sub.userNumber)" class="fas fa-check-circle account-card__check"></i>
      </div>
    </div>
  </b-dropdown>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'SubaccountSwitch',

  data() {
    return {
      loader: null,
    }
  },

  computed: {
    ...mapState(['customerData', 'customerAccounts']),

    mainAccount() {
      return this.customerAccounts?.mainAccount || {}
    },

    isMainAccountActive() {
      return this.customerData.userNumber === this.mainAccount.userNumber
    },

    subaccounts() {
      return this.customerAccounts?.subaccounts || []
    },

    hasMultipleAccounts() {
      return this.subaccounts.length > 0
    },
  },

  methods: {
    isSubaccountActive(userNumber) {
      return this.customerData.userNumber === userNumber
    },

    switchAccount(userNumber) {
      if (this.customerData.userNumber === userNumber) {
        return
      }

      this.loader = this.$loading.show(this.$_loaderOptions)

      this.$store.dispatch(this.$_actionTypes.SWITCH_CUSTOMER_ACCOUNT, userNumber)
        .then(() => {
          this.$notify({
            group: 'app',
            type: 'success',
            text: this.$t('navigation.customer.account_switcher.switched', { account: userNumber })
          })
          window.location.reload()
        })
        .catch((error) => {
          this.$_notifyError(error, this)
        })
        .finally(() => {
          this.loader.hide()
        })
    },
  },
}
</script>

<style scoped>
.account-switcher {
  display: flex;
  align-items: center;
}

.account-switcher ::v-deep .btn-link {
  padding: 0 !important;
  text-decoration: none !important;
  color: rgba(0, 0, 0, 0.55) !important;
}

.account-switcher ::v-deep .btn-link:hover {
  color: rgba(0, 0, 0, 0.7) !important;
}

.account-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.55);
  border-radius: 0.25rem;
}

.account-trigger:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.account-trigger i:first-child {
  font-size: 1.25rem;
  color: #0066cc;
}

.account-trigger span {
  font-weight: 500;
}

.account-trigger__chevron {
  font-size: 0.75rem;
  margin-left: 0.125rem;
  transition: transform 0.2s ease;
}

.account-switcher ::v-deep .show .account-trigger__chevron {
  transform: rotate(180deg);
}

.account-switcher ::v-deep .dropdown-menu {
  padding: 0 !important;
  border-radius: 0.5rem;
  border: 1px solid #e0e0e0;
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.12);
  min-width: 320px;
  right: auto !important;
  left: -120px !important;
  top: calc(100% + 8px) !important;
  animation: slideDown 0.2s ease;
  overflow: hidden;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-header {
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.dropdown-header__label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dropdown-accounts {
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
}

.dropdown-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e0e0e0, transparent);
  margin: 0.5rem 0;
}

.account-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.account-card:not(.account-card--disabled):hover {
  background-color: #f5f5f5;
  transform: translateX(2px);
}

.account-card--active {
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%);
  border-color: #0066cc;
}

.account-card--disabled {
  cursor: not-allowed;
  opacity: 1;
}

.account-card--disabled:hover {
  background-color: transparent;
  transform: none;
}

.account-card__content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.account-card__number {
  font-weight: 600;
  font-size: 0.95rem;
  color: #212529;
  letter-spacing: -0.3px;
}

.account-card--active .account-card__number {
  color: #0066cc;
}

.account-card__badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: #e3f2fd;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: #0066cc;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.account-card__check {
  width: 1.25rem;
  height: 1.25rem;
  color: #28a745;
  font-size: 1.25rem;
  margin-left: auto;
}

.account-switcher ::v-deep .dropdown-item {
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
}

.account-switcher ::v-deep .dropdown-item:active,
.account-switcher ::v-deep .dropdown-item.active {
  background: transparent !important;
}
</style>
